import { useRef, useState } from "react";
import useSWR from "swr";
import { ArrowDown, ArrowUp, Loader2, Plus, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function MediaTab() {
  const { data: shots, mutate } = useSWR("/screenshots", fetcher);
  const fileRef = useRef(null);
  const replaceRefs = useRef({});
  const [busy, setBusy] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTitleFr, setNewTitleFr] = useState("");

  const withBusy = async (fn) => {
    try {
      setBusy(true);
      await fn();
      await mutate();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("title", newTitle.trim() || "لقطة من التطبيق");
    fd.append("title_fr", newTitleFr.trim());
    fd.append("file", file);
    withBusy(async () => {
      await api.post("/admin/screenshots", fd);
      setNewTitle("");
      setNewTitleFr("");
    });
  };

  const rename = (id, title) =>
    withBusy(async () => {
      const fd = new FormData();
      fd.append("title", title);
      await api.put(`/admin/screenshots/${id}`, fd);
    });

  const renameFr = (id, titleFr) =>
    withBusy(async () => {
      const fd = new FormData();
      fd.append("title_fr", titleFr);
      await api.put(`/admin/screenshots/${id}`, fd);
      toast.success("تم تحديث العنوان الفرنسي");
    });

  const replace = (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    withBusy(async () => {
      await api.put(`/admin/screenshots/${id}`, fd);
      toast.success("تم استبدال الصورة");
    });
  };

  const move = (index, dir) => {
    const list = [...shots];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    withBusy(() => api.put("/admin/screenshots-order", { ids: list.map((s) => s.id) }));
  };

  const remove = (id) => {
    if (!window.confirm("هل تريد حذف هذه اللقطة؟")) return;
    withBusy(() => api.delete(`/admin/screenshots/${id}`));
  };

  return (
    <div className="space-y-6" data-testid="admin-media-tab">
      <div className="rounded-2xl border border-dashed border-gold/50 bg-white p-6" data-testid="admin-upload-card">
        <h3 className="font-head text-base font-extrabold text-forest-900">إضافة لقطة جديدة</h3>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            className="max-w-xs flex-1 rounded-xl border border-line px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none"
            placeholder="عنوان اللقطة (مثال: الرئيسية)"
            value={newTitle}
            data-testid="admin-upload-title"
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <input
            className="max-w-xs flex-1 rounded-xl border border-line px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none"
            placeholder="العنوان بالفرنسية (اختياري)"
            value={newTitleFr}
            data-testid="admin-upload-titlefr"
            onChange={(e) => setNewTitleFr(e.target.value)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="admin-upload-button"
            className="inline-flex items-center gap-2 rounded-full bg-forest-900 px-5 py-2.5 text-sm font-bold text-cream transition hover:bg-forest-800 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            اختر صورة وارفعها
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="admin-upload-file"
            onChange={(e) => {
              upload(e.target.files[0]);
              e.target.value = "";
            }}
          />
        </div>
        <p className="mt-3 text-xs text-moss">الصيغ المدعومة: JPG / PNG / WEBP — بحجم أقصى 5 ميجابايت</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {(shots || []).map((shot, i) => (
          <div key={shot.id} className="flex gap-4 rounded-2xl border border-line bg-white p-4" data-testid={`admin-shot-card-${i}`}>
            <img src={mediaUrl(shot.media_id)} alt={shot.title} className="h-40 w-20 shrink-0 rounded-xl border border-line object-cover" />
            <div className="flex w-full flex-col gap-2">
              <input
                className="rounded-xl border border-line px-3 py-2 text-sm focus:border-gold focus:outline-none"
                defaultValue={shot.title}
                placeholder="العنوان بالعربية"
                data-testid={`admin-shot-title-${i}`}
                onBlur={(e) => e.target.value !== shot.title && rename(shot.id, e.target.value)}
              />
              <input
                className="rounded-xl border border-line px-3 py-2 text-sm focus:border-gold focus:outline-none"
                defaultValue={shot.title_fr || ""}
                placeholder="العنوان بالفرنسية (اختياري)"
                dir="ltr"
                data-testid={`admin-shot-titlefr-${i}`}
                onBlur={(e) => e.target.value !== (shot.title_fr || "") && renameFr(shot.id, e.target.value)}
              />
              <div className="mt-auto flex items-center gap-2">
                <button onClick={() => move(i, -1)} data-testid={`admin-shot-up-${i}`} aria-label="تحريك للأعلى" className="rounded-lg border border-line p-2 text-moss transition hover:border-gold hover:text-gold-dark">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} data-testid={`admin-shot-down-${i}`} aria-label="تحريك للأسفل" className="rounded-lg border border-line p-2 text-moss transition hover:border-gold hover:text-gold-dark">
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => replaceRefs.current[shot.id]?.click()}
                  data-testid={`admin-shot-replace-${i}`}
                  className="inline-flex items-center gap-1 rounded-lg border border-line px-3 py-2 text-xs font-semibold text-moss transition hover:border-gold hover:text-gold-dark"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                  استبدال
                </button>
                <button onClick={() => remove(shot.id)} data-testid={`admin-shot-delete-${i}`} aria-label="حذف" className="rounded-lg border border-line p-2 text-moss transition hover:border-brick hover:text-brick">
                  <Trash2 className="h-4 w-4" />
                </button>
                <input
                  ref={(el) => (replaceRefs.current[shot.id] = el)}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    replace(shot.id, e.target.files[0]);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        {shots && shots.length === 0 && (
          <p className="col-span-2 rounded-xl border border-dashed border-line p-8 text-center text-sm text-moss" data-testid="admin-shots-empty">
            لا توجد لقطات بعد — أضف أول لقطة من الأعلى
          </p>
        )}
      </div>
    </div>
  );
}
